/**
 * RhythmLibrary.js
 * A database of pre-defined rhythmic patterns for various genres and meters.
 * Used to seed the RhythmLogic engine with musical intentionality beyond pure Euclidean math.
 */

export const RHYTHM_PATTERNS = {
    // 4/4 Patterns (16 steps)
    '4/4': {
        'driving_ostinato': [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0], // Straight 8ths
        'action_pulse':     [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0], // 3+3+2 Clave-ish
        'cinematic_chug':   [1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0], // Heavy metal gallop feel
        'heroic_triplets':  [1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 0, 0, 1, 0, 0, 0], // Pseudo-triplet feel
        'syncopated_comp':  [1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1, 0, 0], // Funky/Pop comping
        'sparse_pad':       [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], // Whole note
        'half_pulse':       [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0], // Half notes
        'quarter_pulse':    [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0], // Quarter notes
        'charleston':       [1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], // Dotted Quarter + Eighth
        'tresillo':         [1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0], // 3-3-2 basic
        'motor_16ths':      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], // Machine gun
    },
    // 3/4 Patterns (12 steps)
    '3/4': {
        'waltz_basic':      [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0], // Boom-Chuck-Chuck
        'mazurka':          [1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0], // Accent on beat 2
        'rolling_3':        [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0], // 8ths
        'hemiola':          [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0], // Same as waltz but used for 2-feel
    },
    // 6/8 Patterns (12 steps)
    '6/8': {
        'siciliana':        [1, 0, 1, 0, 0, 0, 1, 0, 1, 0, 0, 0], // Dotted 8th + 16th + 8th
        'tarantella':       [1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 0, 1], // Continuous triplets
        'slow_blues':       [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0], // 12/8 feel actually
    },
    // 5/4 Patterns (20 steps)
    '5/4': {
        'take_five':        [1, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0], // 3+2 feel
        'mission_imp':      [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0], // 3+3+2+2
    }
};

// Helper to get a random pattern for a meter/style
export const getRandomPattern = (timeSig, style, random) => {
    let collection = RHYTHM_PATTERNS['4/4'];
    if (timeSig === 3) collection = RHYTHM_PATTERNS['3/4'];
    else if (timeSig === 6) collection = RHYTHM_PATTERNS['6/8']; // Check subdivision logic

    // Filter by style keywords if provided
    let keys = Object.keys(collection);
    if (style) {
        const filtered = keys.filter(k => k.includes(style));
        if (filtered.length > 0) keys = filtered;
    }

    // Pick random
    const idx = Math.floor((random ? random.next() : Math.random()) * keys.length);
    return collection[keys[idx]];
};
